import Constants from '@/Constants';
import { EntityType } from '@/models/EntityTypes';
import { DynamicPlaceholder, Highlight } from '@/models/Highlight';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import List from '@/models/List';
import { Person } from '@/models/Person';
import { analyticsService, FlowEvents, PlaylistEvents } from '@/services/AnalyticsService';
import http from '@/services/HttpService';
import { formatDate, getDisplayName, getElapsedTime } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { highlightsClear, highlightsRequest, sendHighlightRequest } from '@/store/highlights/actions';
import { HighlightPaginationData } from '@/store/highlights/types';
import { AxiosResponse } from 'axios';
import React, { ReactElement, useEffect, useState } from 'react';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import regexifyString from 'regexify-string';
import _ from 'underscore';
import PersonImage from '../PersonImage';
import './style.scss';

interface Props {
    entity: List | Item;
    type: EntityType;
}
interface UserMention {
    mention: string;
    placeholder: string;
}

function fetchPeople(query: string, callback: (data: SuggestionDataItem[]) => void) {
    if (!query) return;

    http.get(Constants.CAST_PEOPLE_ATTRIBUTES_SEARCH_URI.replace('%t', query).replace('%f', 'id,displayName,firstName,lastName,thumbnail'))
        .then((response: AxiosResponse<Person[]>) =>
            response.data.map((p) => ({ display: getDisplayName(p), id: String(p.id), thumbnail: p.thumbnail })),
        )
        .then(callback);
}

const CommentsSection: React.FC<Props> = (props: Props) => {
    const { entity, type } = props;
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: highlightData } = useSelector((state: ApplicationState) => state.highlights);
    const author = useSelector((state: ApplicationState) => state.identity.data.person);
    const dispatch = useDispatch();
    const history = useHistory();

    const [comment, setComment] = useState<string>('');
    const [usersMentions, setUsersMentions] = useState<UserMention[]>([]);

    const pagination: HighlightPaginationData = {
        offset: 0,
        limit: Constants.COMMENTS_PAGINATION_LIMIT,
        contentType: type === EntityType.CONTENT ? Constants.CONTENTS_RESOURCE_NAME : Constants.COURSES_RESOURCE_NAME,
        contentId: (entity && entity.id) || 0,
    };

    const requestHighLights = () => {
        dispatch(
            highlightsRequest({
                data: {
                    ...pagination,
                    offset: highlightData.highlightList.length,
                },
            }),
        );
    };

    useEffect(() => {
        requestHighLights();
        return () => {
            dispatch(highlightsClear());
        };
    }, []);

    const onLoadMoreCommentsHandler = () => {
        requestHighLights();
    };

    const resolveTimeLabel = (date?: Date): string => {
        if (date) {
            return getElapsedTime(strings._LANGUAGE, date);
        }

        return formatDate(new Date(), strings._LANGUAGE as LanguageType, false, false, '.');
    };

    const navigateToProfile = (externalPersonId: number, type: EntityType) => {
        if (type === EntityType.CONTENT) {
            analyticsService.logEvent(FlowEvents.ACCESS_PROFILE_PLT_COMMENT_ITEM);
        } else {
            analyticsService.logEvent(FlowEvents.ACCESS_PROFILE_PLT_COMMENT_LIST);
        }
        history.push(`/profile/${externalPersonId}/info`);
    };

    const replaceMentionsInComment = (comment: string, placeholderMap: DynamicPlaceholder) => {
        let replacedComment: any = comment;

        if (placeholderMap) {
            replacedComment = regexifyString({
                pattern: new RegExp(/#{person_[0-9]*}#/, 'gm'),
                decorator: function getPublicProfileLink(match) {
                    const mentionedPerson = _.get(placeholderMap, match);
                    return <Link to={`/profile/${mentionedPerson?.id}/info`}>{`@${mentionedPerson?.displayName} `}</Link>;
                },
                input: replacedComment,
            });
        }
        return replacedComment;
    };

    const commentsSectionRenderizer = (): ReactElement => {
        if (highlightData.highlightList.length === 0) {
            return <p className="emptySection">{strings.EMPTY_COMMENTS_SECTION}</p>;
        }

        const commentsLeftToShow: number = highlightData.highlightListSize - highlightData.highlightList.length;

        return (
            <>
                {highlightData.highlightList.map((item: Highlight, index) => (
                    <div className="commentsItem" key={index}>
                        <div className="commentsHeader" onClick={() => navigateToProfile(item.externalPersonId, type)}>
                            {<PersonImage size={40} src={item.externalPerson.thumbnail} />}
                            <div className="userInfo">
                                <div className="commentsUserName">
                                    <p>{getDisplayName(item.externalPerson)}</p>
                                </div>
                                <div className="commentsTime">
                                    <p>{resolveTimeLabel(item.creationDate)}</p>
                                </div>
                            </div>
                        </div>
                        <div className="userCommentTextContainer">
                            <p>{replaceMentionsInComment(item.comment, item.placeholderMap)}</p>
                        </div>
                    </div>
                ))}
                {commentsLeftToShow > 0 && (
                    <button className="loadMoreCommentsButton" onClick={() => onLoadMoreCommentsHandler()} type="button">
                        {strings.SEE_MORE_COMMENTS.replace('%n', commentsLeftToShow.toString())}
                    </button>
                )}
            </>
        );
    };

    const onPressSendHandler = (event: React.FormEvent): void => {
        event.preventDefault();
        if (comment) {
            let replacedComment = comment;
            usersMentions.forEach((it) => {
                replacedComment = replacedComment.replace(`${it.mention}`, it.placeholder);
            });
            dispatch(
                sendHighlightRequest({
                    id: 0,
                    externalPerson: author,
                    content: {
                        id: entity.id,
                        title: entity.name,
                        type: type === EntityType.CONTENT ? Constants.CONTENTS_RESOURCE_NAME : Constants.COURSES_RESOURCE_NAME,
                    },
                    comment: replacedComment,
                } as Highlight),
            );
            if (type === EntityType.CONTENT) {
                analyticsService.logEvent(PlaylistEvents.COMMENT_ITEM, {
                    entityId: entity.id as number,
                    entityName: entity.name,
                    entityType: Constants.entityTypes.CONTENT,
                });
            } else if (type === EntityType.COURSE) {
                analyticsService.logEvent(PlaylistEvents.COMMENT_LIST, {
                    entityId: entity.id as number,
                    entityName: entity.name,
                    entityType: Constants.entityTypes.COURSE,
                });
            }

            setComment('');
        }
    };

    const onChangeMentions = (event: { target: { value: string } }, newValue: string, newPlainTextValue: string, mentions: any[]) => {
        const result = mentions.map((value) => {
            const name = value.display.slice(1);
            return {
                mention: `@[${name}](${value.id})`,
                placeholder: `#{person_${value.id}}#`,
            };
        });
        setComment(newValue);
        setUsersMentions(result);
    };

    const renderSuggestion = (suggestion: SuggestionDataItem) => {
        return (
            <div className="suggested-item">
                <PersonImage size={33} src={(suggestion as Person).thumbnail} />
                <div className="suggested-item-content">{suggestion.display}</div>
            </div>
        );
    };

    return (
        <div className="commentsSection">
            <div className="commentsSectionTitle" id={Constants.COMMENTS_SECTION_ID}>
                <h2>{strings.COMMENTS}</h2>
            </div>
            <form className="commentInputSection" onSubmit={onPressSendHandler}>
                <MentionsInput
                    allowSuggestionsAboveCursor
                    className="commentTextArea"
                    onChange={onChangeMentions}
                    placeholder={strings.COMMENT_PLACEHOLDER}
                    value={comment}
                >
                    <Mention
                        className="commentInputSection__mention"
                        data={fetchPeople}
                        displayTransform={(id, display) => `@${display}`}
                        renderSuggestion={renderSuggestion}
                        trigger="@"
                    />
                </MentionsInput>

                <button className="commentInputButton" type="submit">
                    {strings.ADD_COMMENT}
                </button>
            </form>
            {commentsSectionRenderizer()}
        </div>
    );
};

export default CommentsSection;
